import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Truck, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
  { code: "DC", name: "District of Columbia" },
];

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cartLoading, setCartLoading] = useState(true);
  const [cart, setCart] = useState(null);
  const [product, setProduct] = useState(null);
  
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
  });

  const [errors, setErrors] = useState({});

  // Fetch cart and product data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const cartId = localStorage.getItem("nukleus_cart_id");
        if (!cartId) {
          navigate("/");
          return;
        }

        const [cartRes, productRes] = await Promise.all([
          axios.get(`${API}/cart/${cartId}`),
          axios.get(`${API}/product`)
        ]);

        if (!cartRes.data.items?.length) {
          toast.error("Your cart is empty");
          navigate("/");
          return;
        }

        setCart(cartRes.data);
        setProduct(productRes.data);

        // Fire InitiateCheckout pixel event
        if (window.fbq) {
          window.fbq('track', 'InitiateCheckout', {
            content_ids: cartRes.data.items.map(item => item.product_id),
            content_type: 'product',
            value: cartRes.data.total,
            currency: 'USD',
            num_items: cartRes.data.items.reduce((sum, item) => sum + item.quantity, 0)
          });
        }
      } catch (e) {
        console.error("Error fetching data:", e);
        toast.error("Failed to load checkout");
        navigate("/");
      } finally {
        setCartLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleStateChange = (value) => {
    setFormData(prev => ({ ...prev, state: value }));
    if (errors.state) {
      setErrors(prev => ({ ...prev, state: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Valid email is required";
    }
    if (!formData.firstName.trim()) newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.address1.trim()) newErrors.address1 = "Address is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.state) newErrors.state = "State is required";
    if (!formData.zip || !/^\d{5}(-\d{4})?$/.test(formData.zip)) {
      newErrors.zip = "Valid ZIP code is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      const cartId = localStorage.getItem("nukleus_cart_id");
      
      const response = await axios.post(`${API}/create-shopify-checkout`, {
        cart_id: cartId,
        customer: {
          email: formData.email,
          first_name: formData.firstName,
          last_name: formData.lastName,
          address1: formData.address1,
          address2: formData.address2,
          city: formData.city,
          province: formData.state,
          zip: formData.zip,
          country: "US",
          phone: formData.phone,
        }
      });

      if (response.data.checkout_url) {
        // Redirect to Shopify checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (e) {
      console.error("Checkout error:", e);
      toast.error(e.response?.data?.detail || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  if (cartLoading) {
    return (
      <div className="min-h-screen bg-nukleus-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-nukleus-violet border-t-transparent rounded-full animate-spin" />
          <p className="text-white/50">Loading checkout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-nukleus-bg" data-testid="checkout-page">
      {/* Header */}
      <header className="border-b border-white/10 py-4">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            data-testid="back-to-shop"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to shop</span>
          </button>
          <span className="text-white font-bold text-lg tracking-tight">NUKLEUS</span>
          <div className="w-24" /> {/* Spacer for centering */}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left - Form */}
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-white mb-2">
              Checkout
            </h1>
            <p className="text-white/50 mb-8">
              Enter your shipping details to continue
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact */}
              <div>
                <h2 className="text-lg font-medium text-white mb-4">Contact</h2>
                <div>
                  <Label htmlFor="email" className="text-white/70">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@example.com"
                    className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                    data-testid="checkout-email"
                  />
                  {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
                </div>
              </div>

              {/* Shipping */}
              <div>
                <h2 className="text-lg font-medium text-white mb-4">Shipping Address</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName" className="text-white/70">First Name</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="John"
                        className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                        data-testid="checkout-firstname"
                      />
                      {errors.firstName && <p className="text-red-400 text-sm mt-1">{errors.firstName}</p>}
                    </div>
                    <div>
                      <Label htmlFor="lastName" className="text-white/70">Last Name</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Doe"
                        className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                        data-testid="checkout-lastname"
                      />
                      {errors.lastName && <p className="text-red-400 text-sm mt-1">{errors.lastName}</p>}
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address1" className="text-white/70">Address</Label>
                    <Input
                      id="address1"
                      name="address1"
                      value={formData.address1}
                      onChange={handleInputChange}
                      placeholder="123 Main St"
                      className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                      data-testid="checkout-address1"
                    />
                    {errors.address1 && <p className="text-red-400 text-sm mt-1">{errors.address1}</p>}
                  </div>

                  <div>
                    <Label htmlFor="address2" className="text-white/70">Apartment, suite, etc. (optional)</Label>
                    <Input
                      id="address2"
                      name="address2"
                      value={formData.address2}
                      onChange={handleInputChange}
                      placeholder="Apt 4B"
                      className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                      data-testid="checkout-address2"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city" className="text-white/70">City</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="New York"
                        className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                        data-testid="checkout-city"
                      />
                      {errors.city && <p className="text-red-400 text-sm mt-1">{errors.city}</p>}
                    </div>
                    <div>
                      <Label htmlFor="state" className="text-white/70">State</Label>
                      <Select value={formData.state} onValueChange={handleStateChange}>
                        <SelectTrigger className="mt-1 bg-white/5 border-white/10 text-white" data-testid="checkout-state">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent className="bg-nukleus-card border-white/10">
                          {US_STATES.map(state => (
                            <SelectItem key={state.code} value={state.code} className="text-white hover:bg-white/10">
                              {state.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.state && <p className="text-red-400 text-sm mt-1">{errors.state}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="zip" className="text-white/70">ZIP Code</Label>
                      <Input
                        id="zip"
                        name="zip"
                        value={formData.zip}
                        onChange={handleInputChange}
                        placeholder="10001"
                        className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                        data-testid="checkout-zip"
                      />
                      {errors.zip && <p className="text-red-400 text-sm mt-1">{errors.zip}</p>}
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-white/70">Phone (optional)</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(555) 123-4567"
                        className="mt-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
                        data-testid="checkout-phone"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-nukleus-violet hover:bg-nukleus-violet/90 text-white rounded-full py-6 text-base font-semibold shadow-[0_0_30px_rgba(124,58,237,0.4)] hover:shadow-[0_0_40px_rgba(124,58,237,0.6)] transition-all disabled:opacity-50"
                data-testid="checkout-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Continue to Payment — ${cart?.total?.toFixed(2)}
                  </span>
                )}
              </Button>

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-6 text-white/40 text-sm">
                <span className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Secure checkout
                </span>
                <span className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  30-day guarantee
                </span>
              </div>
            </form>
          </div>

          {/* Right - Order Summary */}
          <div className="lg:pl-8 lg:border-l lg:border-white/10">
            <div className="bg-nukleus-card rounded-2xl p-6 border border-white/5 sticky top-8">
              <h2 className="text-lg font-medium text-white mb-6">Order Summary</h2>

              {/* Items */}
              <div className="space-y-4 mb-6">
                {cart?.items?.map((item) => (
                  <div key={item.id} className="flex gap-4">
                    <div className="w-16 h-16 bg-white/5 rounded-lg flex items-center justify-center shrink-0 overflow-hidden">
                      <img
                        src="https://customer-assets.emergentagent.com/job_focus-powder/artifacts/5l4mwejz_Hero%20shot.jpg"
                        alt={item.product_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-medium text-sm">{item.product_name}</h3>
                      <p className="text-white/50 text-xs">{item.variant}</p>
                      {item.is_subscription && (
                        <span className="text-nukleus-lilac text-xs">Subscribe & Save</span>
                      )}
                      <p className="text-white/50 text-xs mt-1">Qty: {item.quantity}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-medium">
                        ${(item.price * item.quantity * (1 - item.discount_percent / 100)).toFixed(2)}
                      </p>
                      {item.discount_percent > 0 && (
                        <p className="text-white/30 text-xs line-through">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="border-t border-white/10 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Subtotal</span>
                  <span className="text-white">${cart?.subtotal?.toFixed(2)}</span>
                </div>
                {cart?.discount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-nukleus-lilac">Subscription Discount</span>
                    <span className="text-nukleus-lilac">-${cart?.discount?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-white/50">Shipping</span>
                  <span className="text-nukleus-lilac">FREE</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-white/10">
                  <span className="text-white font-medium">Total</span>
                  <span className="text-white font-semibold text-lg">${cart?.total?.toFixed(2)}</span>
                </div>
              </div>

              {/* Shipping info */}
              <div className="mt-6 p-4 bg-white/5 rounded-xl">
                <div className="flex items-center gap-3 text-white/70">
                  <Truck className="w-5 h-5 text-nukleus-violet" />
                  <div>
                    <p className="text-sm font-medium text-white">Free US Shipping</p>
                    <p className="text-xs text-white/50">Estimated delivery: 3-5 business days</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
